import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";
import AiModuleTabs from "../components/AiModuleTabs";

export default function AiLoveAssistantPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [chat, setChat] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "AI恋爱助手 | 聊天记录分析",
    metaDescription: "粘贴聊天记录，分析关系阶段、互动积极度与沟通建议。",
    path: "/ai/love-assistant",
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/love-assistant")}`, {
        replace: true,
      });
    }
  }, [userId, navigate]);

  const loadHistory = async () => {
    if (!userId) return;
    try {
      const { data } = await apiJson(
        `/api/ai/love-assistant/history?user_id=${userId}&limit=8`
      );
      setHistory(data?.items || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const analyze = async (e) => {
    e?.preventDefault();
    if (!chat.trim() || loading) return;
    setLoading(true);
    try {
      const { res, data } = await apiJson("/api/ai/love-assistant/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, chat_content: chat.trim() }),
      });
      if (!res.ok) throw new Error(data?.detail || "分析失败");
      setResult(data.item);
      await loadHistory();
    } catch (err) {
      alert(err.message || "分析失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-orange-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8 space-y-6">
        <AiModuleTabs groupKey="advisor" />
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900">AI 恋爱助手</h1>
          <p className="text-sm text-gray-500 mt-2">
            粘贴你们的聊天记录，分析关系阶段、对方互动积极度，并给出可执行的沟通建议。
          </p>
        </div>

        <form onSubmit={analyze} className="bg-white rounded-2xl border border-rose-100 p-5 shadow-sm space-y-3">
          <textarea
            value={chat}
            onChange={(e) => setChat(e.target.value)}
            rows={10}
            placeholder={`示例：\n我：周末有空吗？\n对方：可能有点忙\n我：那下周末呢？想请你喝咖啡\n对方：可以呀`}
            className="w-full border rounded-xl px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={loading || chat.trim().length < 10}
              className="px-5 py-2.5 rounded-full bg-rose-500 text-white text-sm disabled:opacity-50"
            >
              {loading ? "分析中…" : "开始 AI 分析"}
            </button>
            <Link
              to="/ai/consultant"
              className="px-4 py-2.5 rounded-full border border-gray-200 text-gray-600 text-sm"
            >
              去问 AI 咨询师
            </Link>
          </div>
        </form>

        {result && (
          <div className="bg-white rounded-2xl border border-rose-100 p-6 shadow-sm space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-gray-400">当前关系阶段</div>
                <div className="text-2xl font-bold text-gray-900">
                  {result.relationship_stage}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-extrabold text-rose-600">
                  {result.engagement_score ?? "—"}%
                </div>
                <div className="text-xs text-gray-400">对方互动积极度</div>
              </div>
            </div>
            <p className="text-sm text-gray-700 bg-rose-50 rounded-xl p-4">
              {result.emotion_analysis}
            </p>
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">建议</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                {(result.suggestions || []).map((s) => (
                  <li key={s} className="flex gap-2">
                    <span className="text-rose-500">→</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="space-y-2">
            <h2 className="font-semibold text-gray-800">最近分析</h2>
            {history.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => setResult(h)}
                className="w-full text-left bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm hover:border-rose-200"
              >
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-gray-800">
                    {h.relationship_stage}
                    {h.engagement_score != null ? ` · ${h.engagement_score}%` : ""}
                  </span>
                  <span className="text-xs text-gray-400">
                    {h.created_at ? h.created_at.slice(0, 16).replace("T", " ") : ""}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{h.preview}</p>
              </button>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
